import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "@/components/common/PageHeader";
import { subscriptionService } from "@/services/subscription.service";
import { moduleService } from "@/services/module.service";
import type { SystemModule, ModuleCategory } from "@/types/modules";
import { MODULE_CATEGORIES } from "@/types/modules";
import type { PlanSummary } from "@/types";
import toast from "react-hot-toast";

function PlanModulesPage() {
  const queryClient = useQueryClient();
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [moduleSearchTerm, setModuleSearchTerm] = useState("");

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ["admin-plans", "all"],
    queryFn: () => subscriptionService.listPlans(true),
  });

  const { data: modules = [], isLoading: modulesLoading } = useQuery({
    queryKey: ["admin-modules"],
    queryFn: moduleService.getAllModules,
  });

  const { data: planModules, refetch: refetchPlanModules } = useQuery({
    queryKey: ["admin-plan-modules", selectedPlanId],
    queryFn: () => moduleService.getPlanModules(selectedPlanId),
    enabled: Boolean(selectedPlanId),
  });

  useEffect(() => {
    if (!selectedPlanId && plans.length > 0) {
      setSelectedPlanId(plans[0].id);
    }
  }, [plans, selectedPlanId]);

  useEffect(() => {
    if (!planModules) return;
    setSelectedModules(planModules.map((m) => m.code));
  }, [planModules]);

  const { mutateAsync: saveModules, isPending: isSaving } = useMutation({
    mutationFn: async () => {
      if (!selectedPlanId) return;
      await moduleService.updatePlanModules(selectedPlanId, selectedModules);
    },
    onSuccess: async () => {
      await refetchPlanModules();
      queryClient.invalidateQueries({ queryKey: ["admin-plans"] });
      toast.success("Plan modules updated");
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update plan modules";
      toast.error(message);
    },
  });

  const filteredModules = useMemo(() => {
    if (!modules.length) return [] as SystemModule[];
    if (!moduleSearchTerm) return modules;
    const term = moduleSearchTerm.toLowerCase();
    return modules.filter(
      (m) =>
        m.name.toLowerCase().includes(term) ||
        m.code.toLowerCase().includes(term),
    );
  }, [modules, moduleSearchTerm]);

  const groupedModules = useMemo(() => {
    return filteredModules.reduce(
      (acc, module) => {
        const category = module.category as ModuleCategory;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(module);
        return acc;
      },
      {} as Record<ModuleCategory, SystemModule[]>,
    );
  }, [filteredModules]);

  const toggleModule = (moduleCode: string) => {
    setSelectedModules((prev) =>
      prev.includes(moduleCode)
        ? prev.filter((c) => c !== moduleCode)
        : [...prev, moduleCode],
    );
  };

  const currentPlan = plans.find((p) => p.id === selectedPlanId);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Plan Modules"
        subtitle="Assign system modules to subscription plans."
      />

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="grid gap-6 p-6 lg:grid-cols-[260px_1fr]">
          <aside className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Plans</h3>
              <p className="text-xs text-slate-500">
                Select a plan to edit its module access.
              </p>
            </div>
            <div className="space-y-2">
              {plansLoading ? (
                <p className="text-xs text-slate-500">Loading plans...</p>
              ) : plans.length ? (
                plans.map((plan: PlanSummary) => {
                  const isSelected = plan.id === selectedPlanId;
                  return (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => setSelectedPlanId(plan.id)}
                      className={`w-full rounded-xl border px-3 py-2 text-left text-sm transition ${
                        isSelected
                          ? "border-slate-900 bg-slate-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <p className="font-semibold text-slate-900">
                        {plan.name}
                      </p>
                      <p className="text-xs text-slate-500">{plan.type}</p>
                    </button>
                  );
                })
              ) : (
                <p className="text-xs text-slate-500">No plans found.</p>
              )}
            </div>
          </aside>

          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {currentPlan?.name ?? "Select a plan"}
                </h3>
                <p className="text-xs text-slate-500">
                  {currentPlan?.description ||
                    "Choose a plan to view and edit module access."}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedModules([])}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setSelectedModules(modules.map((module) => module.code))
                  }
                  className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600"
                  disabled={modules.length === 0}
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => saveModules()}
                  disabled={!selectedPlanId || isSaving}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isSaving ? "Saving..." : "Save changes"}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-600">
                Search modules
              </label>
              <input
                value={moduleSearchTerm}
                onChange={(event) => setModuleSearchTerm(event.target.value)}
                placeholder="Search by module name or code"
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
              />
            </div>

            <div className="space-y-6">
              {modulesLoading ? (
                <p className="text-sm text-slate-500">Loading modules...</p>
              ) : (
                Object.entries(groupedModules).map(([category, list]) => (
                  <div key={category} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900">
                          {MODULE_CATEGORIES[category as ModuleCategory]
                            ?.label || category}
                        </h4>
                        <p className="text-xs text-slate-500">
                          {MODULE_CATEGORIES[category as ModuleCategory]
                            ?.description || ""}
                        </p>
                      </div>
                      <span
                        className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide ${
                          MODULE_CATEGORIES[category as ModuleCategory]
                            ?.color || "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {list.length} modules
                      </span>
                    </div>

                    <div className="grid gap-3 lg:grid-cols-2">
                      {list.map((module) => (
                        <label
                          key={module.code}
                          className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm transition hover:border-slate-300 ${
                            selectedModules.includes(module.code)
                              ? "border-slate-900 bg-slate-50"
                              : "border-slate-200"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedModules.includes(module.code)}
                            onChange={() => toggleModule(module.code)}
                            className="mt-1"
                          />
                          <div>
                            <p className="font-semibold text-slate-900">
                              {module.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {module.code}
                              {module.description
                                ? ` • ${module.description}`
                                : ""}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PlanModulesPage;
